import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DateUtils from '../utils/dateUtils';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  Collapse,
  Grid,
  Card,
  CardContent,
  Divider,
  TextField,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  LinearProgress,
  MenuItem,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Send as SendIcon,
  AttachMoney as AttachMoneyIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  BarChart as BarChartIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Add as AddIcon
} from '@mui/icons-material';
import MainLayout from '../layout/MainLayout';

// API-Basis-URL
const API_BASE_URL = window.location.hostname === 'localhost' ? 'http://localhost:3001/api' : '/api';
console.log('MyRequestsPage - API-Basis-URL:', API_BASE_URL);

// Hilfsfunktion zum Formatieren von Zahlen mit Tausendertrennzeichen
const formatNumber = (number) => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

// Hilfsfunktion zum Ermitteln der Statusfarbe und des Textes
const getStatusInfo = (status) => {
  switch (status) {
    case 'submitted':
      return { color: 'info', label: 'Eingereicht', icon: <AssignmentIcon fontSize="small" /> };
    case 'in_review':
      return { color: 'warning', label: 'In Prüfung', icon: <PendingIcon fontSize="small" /> };
    case 'approved':
      return { color: 'success', label: 'Genehmigt', icon: <CheckCircleIcon fontSize="small" /> };
    case 'processing':
      return { color: 'info', label: 'In Bearbeitung', icon: <BarChartIcon fontSize="small" /> };
    case 'completed':
      return { color: 'success', label: 'Abgeschlossen', icon: <CheckCircleIcon fontSize="small" /> };
    case 'rejected':
      return { color: 'error', label: 'Abgelehnt', icon: <ErrorIcon fontSize="small" /> };
    default:
      return { color: 'default', label: status, icon: <AssignmentIcon fontSize="small" /> };
  }
};

// Komponente für eine einzelne Tabellenzeile mit erweiterbaren Details
const RequestRow = ({ request }) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [newMessage, setNewMessage] = useState('');
  
  const statusInfo = getStatusInfo(request.status);
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleSendMessage = () => {
    if (newMessage.trim()) {
      // Hier würde in einer realen Anwendung die Nachricht gesendet werden
      console.log('Sending message:', newMessage);
      setNewMessage('');
    }
  };
  
  return (
    <React.Fragment>
      <TableRow 
        sx={{ 
          '& > *': { borderBottom: 'unset' },
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'rgba(255, 122, 0, 0.04)' },
        }}
        onClick={() => setOpen(!open)}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(!open);
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {request.id}
        </TableCell>
        <TableCell>{request.projectId}</TableCell>
        <TableCell>{request.name}</TableCell>
        <TableCell>
          <Chip
            icon={statusInfo.icon}
            label={statusInfo.label}
            color={statusInfo.color}
            size="small"
            variant="outlined"
          />
        </TableCell>
        <TableCell>{DateUtils.formatDate(request.createdAt, 'dd. MMM yyyy HH:mm')}</TableCell>
        <TableCell>
          {request.price ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                {formatNumber(request.price)} €
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Tooltip title="Chat">
            <IconButton size="small" color="primary">
              <Badge badgeContent={request.messages ? request.messages.length : 0} color="primary">
                <ChatBubbleOutlineIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Tabs value={activeTab} onChange={handleTabChange} aria-label="request details tabs">
                <Tab label="Details" />
                <Tab label="Verlauf" />
                <Tab label="Chat" />
                {request.results.available && <Tab label="Ergebnisse" />}
              </Tabs>
              
              <Box sx={{ mt: 2 }}>
                {activeTab === 0 && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                            Anfrageinformationen
                          </Typography>
                          <Divider sx={{ mb: 2 }} />
                          <Grid container spacing={2}>
                            <Grid item xs={4}>
                              <Typography variant="body2" color="text.secondary">
                                Anfrage-ID:
                              </Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <Typography variant="body2">
                                {request.id}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" color="text.secondary">
                                Projekt-ID:
                              </Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <Typography variant="body2">
                                {request.projectId}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" color="text.secondary">
                                Name:
                              </Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <Typography variant="body2">
                                {request.name}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" color="text.secondary">
                                Status:
                              </Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <Chip
                                icon={statusInfo.icon}
                                label={statusInfo.label}
                                color={statusInfo.color}
                                size="small"
                                variant="outlined"
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" color="text.secondary">
                                Erstellt am:
                              </Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <Typography variant="body2">
                                {DateUtils.formatDate(request.createdAt, 'dd. MMM yyyy HH:mm')}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" color="text.secondary">
                                Aktualisiert am:
                              </Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <Typography variant="body2">
                                {DateUtils.formatDate(request.updatedAt, 'dd. MMM yyyy HH:mm')}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" color="text.secondary">
                                Krankenhaus:
                              </Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <Typography variant="body2">
                                {request.hospital}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" color="text.secondary">
                                Preis:
                              </Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <Typography variant="body2">
                                {request.price ? `${formatNumber(request.price)} €` : 'Noch nicht festgelegt'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                            Kohorte
                          </Typography>
                          <Divider sx={{ mb: 2 }} />
                          <Typography variant="body2" gutterBottom>
                            <strong>Name:</strong> {request.cohort.name}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Definition:</strong>
                          </Typography>
                          <Paper variant="outlined" sx={{ p: 1, mt: 1, bgcolor: 'background.default' }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {request.cohort.conditionString}
                            </Typography>
                          </Paper>
                        </CardContent>
                      </Card>
                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                            Variablen
                          </Typography>
                          <Divider sx={{ mb: 2 }} />
                          <Typography variant="body2" gutterBottom>
                            <strong>Name:</strong> {request.variables.name}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Anzahl Variablen:</strong> {request.variables.count}
                          </Typography>
                        </CardContent>
                      </Card>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                            Analyseplan
                          </Typography>
                          <Divider sx={{ mb: 2 }} />
                          <Typography variant="body2" gutterBottom>
                            <strong>Name:</strong> {request.analysis.name}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Typ:</strong> {request.analysis.type}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}
                
                {activeTab === 1 && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Verlauf der Anfrage
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ position: 'relative', ml: 2, mb: 4 }}>
                      {/* Vertikale Linie für den Zeitstrahl */}
                      <Box sx={{ 
                        position: 'absolute', 
                        left: 16, 
                        top: 0, 
                        bottom: 0, 
                        width: 2, 
                        bgcolor: 'divider',
                        zIndex: 0
                      }} />
                      
                      {request.timeline.map((event, index) => {
                        const statusInfo = getStatusInfo(event.status);
                        return (
                          <Box key={index} sx={{ display: 'flex', mb: 3, position: 'relative' }}>
                            <Box sx={{ 
                              width: 36, 
                              height: 36, 
                              borderRadius: '50%', 
                              bgcolor: `${statusInfo.color}.light`, 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              zIndex: 1,
                              boxShadow: 1
                            }}>
                              {statusInfo.icon}
                            </Box>
                            <Box sx={{ ml: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {statusInfo.label}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {DateUtils.formatDate(event.date, 'dd. MMM yyyy HH:mm')}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {event.message}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
                
                {activeTab === 2 && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Chat mit ARGUS und {request.hospital}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ 
                      height: 300, 
                      overflowY: 'auto', 
                      bgcolor: 'background.default', 
                      p: 2, 
                      borderRadius: 1,
                      mb: 2
                    }}>
                      {request.messages.map((message) => (
                        <Box 
                          key={message.id} 
                          sx={{ 
                            display: 'flex', 
                            mb: 2,
                            flexDirection: message.sender === 'user' ? 'row-reverse' : 'row'
                          }}
                        >
                          <Avatar 
                            sx={{ 
                              bgcolor: message.sender === 'user' 
                                ? 'primary.main' 
                                : message.sender === 'hospital' 
                                  ? 'secondary.main' 
                                  : 'grey.500',
                              width: 36,
                              height: 36
                            }}
                          >
                            {message.sender === 'user' 
                              ? 'U' 
                              : message.sender === 'hospital' 
                                ? 'H' 
                                : 'S'}
                          </Avatar>
                          <Paper 
                            variant="outlined" 
                            sx={{ 
                              p: 1.5, 
                              ml: message.sender === 'user' ? 0 : 1,
                              mr: message.sender === 'user' ? 1 : 0,
                              maxWidth: '80%',
                              bgcolor: message.sender === 'user' 
                                ? 'rgba(255, 122, 0, 0.05)' 
                                : 'white',
                              borderColor: message.sender === 'user' 
                                ? 'primary.light' 
                                : 'divider'
                            }}
                          >
                            <Typography variant="body2">
                              {message.text}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}>
                              {DateUtils.formatDate(message.timestamp, 'dd. MMM yyyy HH:mm')}
                            </Typography>
                          </Paper>
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Nachricht eingeben..."
                        variant="outlined"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleSendMessage();
                          }
                        }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        endIcon={<SendIcon />}
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim()}
                      >
                        Senden
                      </Button>
                    </Box>
                  </Box>
                )}
                
                {activeTab === 3 && request.results.available && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Analyseergebnisse
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Card variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          Zusammenfassung
                        </Typography>
                        <Typography variant="body2" paragraph>
                          {request.results.summary}
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<DownloadIcon />}
                          href={request.results.downloadUrl}
                        >
                          Ergebnisse herunterladen
                        </Button>
                      </CardContent>
                    </Card>
                    {/* Visualisierungen wurden entfernt */}
                  </Box>
                )}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

// Hauptkomponente für die "Meine Anfragen"-Seite
const MyRequestsPage = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Daten vom Backend laden
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log('MyRequestsPage - Starte API-Anfragen...');
        
        // Einzelne Anfragen mit besserer Fehlerbehandlung
        let requestsData = [], statsData = {};
        
        try {
          console.log('MyRequestsPage - Rufe Anfragen ab...');
          const requestsResponse = await axios.get(`${API_BASE_URL}/requests`);
          console.log('MyRequestsPage - Anfragen erhalten:', requestsResponse.status);
          requestsData = requestsResponse.data || [];
          
          // Überprüfen des Datumsformats
          if (requestsData.length > 0) {
            console.log('Datum aus API:', requestsData[0].createdAt);
            console.log('Formatiertes Datum:', DateUtils.formatDate(requestsData[0].createdAt, 'dd.MM.yyyy HH:mm'));
          }
        } catch (err) {
          console.error('MyRequestsPage - Fehler beim Abrufen der Anfragen:', err);
          throw err;
        }
        
        try {
          console.log('MyRequestsPage - Rufe Statistiken ab...');
          const statsResponse = await axios.get(`${API_BASE_URL}/stats`);
          console.log('MyRequestsPage - Statistiken erhalten:', statsResponse.status);
          statsData = statsResponse.data || {};
        } catch (err) {
          console.error('MyRequestsPage - Fehler beim Abrufen der Statistiken:', err);
          throw err;
        }
        
        setRequests(requestsData);
        setStats(statsData);
      } catch (err) {
        console.error('Fehler beim Laden der Daten:', err);
        setError('Die Daten konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshTrigger]);

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };
  
  // Filtern der Anfragen basierend auf Suchbegriff und Statusfilter
  const filteredRequests = requests.filter(request => {
    const matchesSearch = 
      searchTerm === '' || 
      request.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.projectId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });
  
  // Paginierte Anfragen
  const paginatedRequests = filteredRequests.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Statusfilter-Optionen
  const statusOptions = [
    { value: 'all', label: 'Alle Status' },
    { value: 'submitted', label: 'Eingereicht' },
    { value: 'in_review', label: 'In Prüfung' },
    { value: 'approved', label: 'Genehmigt' },
    { value: 'processing', label: 'In Bearbeitung' },
    { value: 'completed', label: 'Abgeschlossen' },
    { value: 'rejected', label: 'Abgelehnt' }
  ];
  
  return (
    <MainLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
          Meine Datenanfragen
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Verwalten und verfolgen Sie Ihre Datenanfragen und kommunizieren Sie mit den Partnerkliniken.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Suche nach ID oder Name..."
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status Filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                InputProps={{
                  startAdornment: <FilterListIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                sx={{ mr: 1 }}
                onClick={handleRefresh}
                disabled={loading}
              >
                Aktualisieren
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                href="/request"
              >
                Neue Anfrage
              </Button>
            </Grid>
          </Grid>
        </Paper>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto', mb: 4, maxWidth: '100%' }}>
            <Table aria-label="collapsible table" sx={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell width="40px" />
                  <TableCell width="12%">Anfrage-ID</TableCell>
                  <TableCell width="12%">Projekt-ID</TableCell>
                  <TableCell width="26%">Name</TableCell>
                  <TableCell width="12%">Status</TableCell>
                  <TableCell width="15%">Erstellt am</TableCell>
                  <TableCell width="12%">Preis</TableCell>
                  <TableCell width="8%">Chat</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRequests.length > 0 ? (
                  paginatedRequests.map((request) => (
                    <RequestRow key={request.id} request={request} />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        Keine Anfragen gefunden
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Passen Sie Ihre Filterkriterien an oder erstellen Sie eine neue Anfrage
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        href="/request"
                        sx={{ mt: 2 }}
                      >
                        Neue Anfrage erstellen
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredRequests.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Zeilen pro Seite:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
            />
          </TableContainer>
        )}
        
        {!loading && stats && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Statistiken
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: 'primary.light', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {stats.totalRequests}
                    </Typography>
                    <Typography variant="body2">
                      Gesamtanfragen
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: 'success.light', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {stats.completedRequests}
                    </Typography>
                    <Typography variant="body2">
                      Abgeschlossene Anfragen
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: 'warning.light', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {stats.processingRequests + stats.approvedRequests + stats.inReviewRequests}
                    </Typography>
                    <Typography variant="body2">
                      Laufende Anfragen
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: 'error.light', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {stats.rejectedRequests}
                    </Typography>
                    <Typography variant="body2">
                      Abgelehnte Anfragen
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </MainLayout>
  );
};

export default MyRequestsPage;
